/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React, { useState, useRef, useEffect } from 'react'
import Text from '../../Text'
import Button from '../../Button'
import { TextInput } from '../../TextInput'
import ProgressHeader from '../components/ProgressHeader'
import {
  useCompetitionSetup,
  Division,
} from '../context/CompetitionSetupContext'
import TrophyIcon from '../icons/TrophyIcon'
import ChevronDownIcon from '../icons/ChevronDownIcon'
import ChevronRightIcon from '../icons/ChevronRightIcon'
import MoreIcon from '../icons/MoreIcon'
import PlusCircleIcon from '../icons/PlusCircleIcon'
import colors from '@sportninja/common/constants/appColors'
import { Dropdown } from 'antd'
import { trackCompetitionDivision } from 'src/utils/analytics'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { EditOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CompetitionContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow-y: auto;
  max-height: 100vh;
`

const CompetitionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

const CompetitionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const DivisionsList = styled.div<{ isAddingNew: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  /* padding-left: 24px; */
  overflow-y: auto;
`

const DivisionItem = styled.div<{ isDragging?: boolean; isEditing?: boolean }>`
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
  padding: 8px 8px 8px ${(props) => (props.isEditing ? '0px' : '12px')};
  display: flex;
  align-items: center;
  gap: ${(props) => (props.isEditing ? '8px' : '12px')};
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  cursor: move;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`

const DragHandle = styled.div`
  color: ${colors.NEUTRAL_100};
  cursor: grab;
  display: flex;
  align-items: center;

  &:active {
    cursor: grabbing;
  }
`

const DivisionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DivisionActions = styled.div`
  display: flex;
  align-items: center;
`

const AddDivisionButton = styled.button`
  background: ${colors.SECONDARY_300};
  border: none;
  border-radius: 12px;
  padding: 16px 16px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${colors.SECONDARY_200};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const InputContainer = styled.div<{ isEditing: boolean; isAddingNew: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;
  background-color: ${colors.SECONDARY_300};
  border-radius: 12px;
  padding: ${(props) =>
    props.isAddingNew
      ? props.isEditing
        ? '0 8px 8px 8px'
        : '0 8px 8px 8px'
      : props.isEditing
      ? '0 8px 8px 8px'
      : '8px'};
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY_300};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

interface DraggableDivisionProps {
  division: Division
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  isEditing: boolean
  onSaveEdit: () => void
  onCancelEdit: () => void
  tempEditingName: string
  setTempEditingName: (name: string) => void
}

const DraggableDivision: React.FC<DraggableDivisionProps> = ({
  division,
  onEdit,
  onDelete,
  isEditing,
  onSaveEdit,
  onCancelEdit,
  tempEditingName,
  setTempEditingName,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: division.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => onEdit(division.id),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => onDelete(division.id),
    },
  ]

  return (
    <div ref={setNodeRef} style={style}>
      <DivisionItem isDragging={isDragging} isEditing={isEditing}>
        <DragHandle {...attributes} {...listeners}>
          {!isEditing ? <MenuOutlined /> : null}
        </DragHandle>
        <DivisionContent>
          {isEditing ? (
            <InputContainer isEditing={isEditing} isAddingNew={false}>
              <TextInput
                name='edit_division'
                value={tempEditingName}
                setValue={setTempEditingName}
                fieldErrors={{}}
                customError={null}
                label=''
                placeholder='Division name'
              />
              <ButtonContainer>
                <Button
                  text='Save'
                  onClick={onSaveEdit}
                  variant='primary'
                  style={css`
                    flex: 1;
                  `}
                />
                <Button
                  text='Cancel'
                  onClick={onCancelEdit}
                  variant='secondary'
                  style={css`
                    flex: 1;
                  `}
                />
              </ButtonContainer>
            </InputContainer>
          ) : (
            <>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {division.name}
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Division
              </Text>
            </>
          )}
        </DivisionContent>
        {!isEditing && (
          <DivisionActions>
            <Dropdown menu={{ items: menuItems }} placement='bottomRight'>
              <button
                css={css`
                  background: transparent;
                  border: none;
                  color: ${colors.PRIMARY_300};
                  cursor: pointer;
                  padding: 4px 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 4px;

                  &:hover {
                    background: ${colors.SECONDARY_400};
                  }
                `}
              >
                <MoreIcon />
              </button>
            </Dropdown>
          </DivisionActions>
        )}
      </DivisionItem>
    </div>
  )
}

/**
 * Divisions Step component - Step 2 of the competition setup wizard
 * Allows users to create and manage divisions
 */
const DivisionsStep: React.FC = () => {
  const {
    state,
    setDivisionsData,
    nextStep,
    previousStep,
    skipWizard,
    competitionName,
  } = useCompetitionSetup()
  const [divisions, setDivisions] = useState<Division[]>(
    state.divisionsData || []
  )
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [newDivisionName, setNewDivisionName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [tempEditingName, setTempEditingName] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)

  // Ref for auto-focus
  const newDivisionInputRef = useRef<any>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setDivisions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const newItems = arrayMove(items, oldIndex, newIndex)
        // Update order values
        return newItems.map((item, index) => ({ ...item, order: index }))
      })
    }
  }

  const handleAddDivision = () => {
    if (newDivisionName.trim()) {
      const newDivision: Division = {
        id: `division_${Date.now()}`,
        name: newDivisionName.trim(),
        order: divisions.length,
      }
      setDivisions([...divisions, newDivision])
      setNewDivisionName('')
      setIsAddingNew(false)
    }
  }

  const handleDivisionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAddDivision()
    }
  }

  const handleEditDivision = (id: string) => {
    const division = divisions.find((d) => d.id === id)
    if (division) {
      setEditingId(id)
      setTempEditingName(division.name)
    }
  }

  const handleSaveEdit = () => {
    if (tempEditingName.trim() && editingId) {
      setDivisions(
        divisions.map((d) =>
          d.id === editingId ? { ...d, name: tempEditingName.trim() } : d
        )
      )
      setEditingId(null)
      setTempEditingName('')
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setTempEditingName('')
  }

  const handleDeleteDivision = (id: string) => {
    // TODO: Replace with proper modal in production
    const confirmed = window.confirm(
      'Are you sure you want to delete this division?'
    )
    if (confirmed) {
      setDivisions(divisions.filter((d) => d.id !== id))
    }
  }

  const handleNext = () => {
    console.log(`🔄 DivisionsStep: Proceeding with ${divisions.length} divisions`)
    
    // Track division setup - we'll track each division name
    divisions.forEach(division => {
      trackCompetitionDivision(division.name)
    })
    
    // Save divisions data to context
    setDivisionsData(divisions)
    nextStep()
  }

  const handleBack = () => {
    previousStep()
  }

  // Auto-focus when adding new division
  useEffect(() => {
    if (isAddingNew && newDivisionInputRef.current) {
      newDivisionInputRef.current.focus()
    }
  }, [isAddingNew])

  return (
    <StepContainer>
      <ProgressHeader currentStep={2} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Create Divisions
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Set up team groupings for the competition tables.
          </Text>
        </TitleSection>

        <CompetitionContainer>
          <CompetitionHeader onClick={() => setIsExpanded(!isExpanded)}>
            <ExpandButton type='button'>
              {isExpanded ? (
                <ChevronDownIcon color={colors.NEUTRAL_100} size={24} />
              ) : (
                <ChevronRightIcon color={colors.NEUTRAL_100} size={24} />
              )}
            </ExpandButton>
            <CompetitionIcon>
              <TrophyIcon color={colors.PRIMARY} size={40} />
            </CompetitionIcon>
            <div>
              <Text
                variant='paragraphMedium'
                weight='bold'
                color={colors.NEUTRAL_0}
              >
                {competitionName}
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Competition
              </Text>
            </div>
          </CompetitionHeader>

          {isExpanded && (
            <DivisionsList isAddingNew={isAddingNew}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={divisions.map((d) => d.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {divisions.map((division) => (
                    <DraggableDivision
                      key={division.id}
                      division={division}
                      onEdit={handleEditDivision}
                      onDelete={handleDeleteDivision}
                      isEditing={editingId === division.id}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={handleCancelEdit}
                      tempEditingName={tempEditingName}
                      setTempEditingName={setTempEditingName}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {isAddingNew ? (
                <InputContainer isEditing={false} isAddingNew={true}>
                  <TextInput
                    ref={newDivisionInputRef}
                    name='new_division'
                    value={newDivisionName}
                    setValue={setNewDivisionName}
                    fieldErrors={{}}
                    customError={null}
                    label=''
                    placeholder='Enter division name'
                    onKeyDown={handleDivisionKeyDown}
                  />
                  <ButtonContainer>
                    <Button
                      text='Add'
                      onClick={handleAddDivision}
                      variant='primary'
                      style={css`
                        flex: 1;
                      `}
                    />
                    <Button
                      text='Cancel'
                      onClick={() => {
                        setIsAddingNew(false)
                        setNewDivisionName('')
                      }}
                      variant='secondary'
                      style={css`
                        flex: 1;
                      `}
                    />
                  </ButtonContainer>
                </InputContainer>
              ) : (
                <AddDivisionButton onClick={() => setIsAddingNew(true)}>
                  <PlusCircleIcon color={colors.PRIMARY_300} size={20} />
                  <Text
                    variant='paragraphMedium'
                    weight='semiBold'
                    color={colors.PRIMARY_300}
                  >
                    Add Division
                  </Text>
                </AddDivisionButton>
              )}
            </DivisionsList>
          )}
        </CompetitionContainer>

        <ActionsContainer>
          <div
            css={css`
              display: flex;
              gap: 16px;
              align-items: center;
              width: 100%;
            `}
          >
            <Button
              text='BACK'
              onClick={handleBack}
              variant='secondary'
              uppercase
              style={css`
                flex: 1;
              `}
            />
            <Button
              text='NEXT'
              onClick={handleNext}
              variant='primary'
              uppercase
              disabled={divisions.length === 0}
              style={css`
                flex: 1;
              `}
            />
          </div>
          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default DivisionsStep
